"use client";

import React, { useEffect, useState } from "react";
import Text from "@components/atoms/Text";
import styles from "./PriceByVariantSection.module.scss";
import { PageType } from "@constants/pageTypes";
import PriceByVariantSectionClientWrapper from "./PriceByVariantSectionClientWrapper";
import { fetchDynamicConfig } from "@services/booking-serivce";
import Image from "@components/atoms/Image";
import assetLoader from "@utils/internalImageLoder";

interface PriceByVariantSectionProps {
  variantsDetails: any;
  carName: string;
  isLoggedIn: boolean;
  city: any;
  brandName: string;
  modelName: string;
  isSellable: boolean;
  pageType?: PageType;
  carAvailability?: string;
  savingBalance?: any;
  fuelType?: string;
}

export default function PriceByVariantSection({
  variantsDetails,
  carName,
  isLoggedIn,
  city,
  brandName,
  modelName,
  isSellable,
  pageType,
  carAvailability,
  savingBalance,
  fuelType,
}: PriceByVariantSectionProps) {
  // GST Banner Configuration
  const [gstConfig, setGstConfig] = useState<any>(null);
  const [shouldShowGST, setShouldShowGST] = useState(false);
  const [isEV, setIsEV] = useState(false);
  const cityName = city?.name;

  // Fetch GST Banner Configuration
  useEffect(() => {
    const fetchGSTConfig = async () => {
      try {
        console.log("🔍 [PriceByVariantSection] Fetching GST config...");
        const config = await fetchDynamicConfig({
          key: "new_car_gst",
          group: "Acko_Drive",
        });
        setGstConfig(config);
      } catch (error) {
        console.error("❌ [PriceByVariantSection] Error fetching GST config:", error);
        setGstConfig(null);
      }
    };

    fetchGSTConfig();
  }, []);

  // Check if GST banner should be shown
  useEffect(() => {
    // Don't show banner if not sellable
    if (!isSellable) {
      setShouldShowGST(false);
      return;
    }

    if (!brandName || !cityName) {
      setShouldShowGST(false);
      return;
    }

    // Check if it's an EV car based on fuel type
    const normalizedBrand = brandName?.toLowerCase?.()?.replace(/\s+/g, '-');
    const isEVCar = fuelType?.toLowerCase() === 'electric';

    // Set EV state
    setIsEV(isEVCar);

    // For EV cars, show banner with EV text
    if (isEVCar) {
      setShouldShowGST(true);
      return;
    }

    // For non-EV cars, check dynamic config
    if (!gstConfig?.data?.new_car_gst?.brands) {
      setShouldShowGST(false);
      return;
    }

    const normalizedCity = cityName?.toLowerCase?.()?.replace(/\s+/g, '-');    
    const brandConfig = gstConfig?.data?.new_car_gst?.brands?.[normalizedBrand];    
    const shouldShow = brandConfig?.cities?.includes(normalizedCity) || false;    
    setShouldShowGST(shouldShow);
  }, [gstConfig, brandName, cityName, isSellable, fuelType]);

  return (
    <div className={styles.sectionWrapper}>
      <div className={styles.headingWrapper}>
        <Text level="h2" className={styles.heading}>
          Price by variants
        </Text>
        <Text level="p" className={styles.subheading}>
          {`${variantsDetails?.length || 0} variants available in ${carName}`}
        </Text>
      </div>
      
      {shouldShowGST && (
        <div className={styles.gstBannerWrapper}>
          <div className={styles.gstBenefitCard}>
            <div className={styles.gstIcon}>
              <Image
                src={`${assetLoader()}/icons/new_car_gst_price.svg`}
                alt="GST Benefit"
                width={52}
                height={40}
              />
            </div>
            <div className={styles.gstText}>
              {isEV 
                ? "No GST impact on EV cars, prices are the same before and after."
                : "Good news! The price you see already includes GST savings."
              }
            </div>
          </div>
        </div>
      )}

      <PriceByVariantSectionClientWrapper
        variantsDetails={variantsDetails}
        carName={carName}
        isLoggedIn={isLoggedIn}
        city={city}
        brandName={brandName}
        modelName={modelName}
        isSellable={isSellable}
        pageType={pageType}
        carAvailability={carAvailability}
        savingBalance={savingBalance}
      />
    </div>
  );
}
